import React, { useState, CSSProperties, ChangeEvent, FocusEvent } from 'react';
import { useAppSelector } from '../../store/hooks';
import CText from './CText';
import { FiEye, FiEyeOff } from 'react-icons/fi';

interface CInputProps {
  value?: string;
  label?: string;
  bgColor?: string;
  inputContainerStyle?: CSSProperties;
  inputBoxStyle?: CSSProperties;
  onChange?: (value: string) => void;
  placeholder?: string;
  type?: string;
  onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  errorText?: string;
  autoFocus?: boolean;
  maxLength?: number;
  editable?: boolean;
  autoCapitalize?: 'off' | 'none' | 'on' | 'sentences' | 'words' | 'characters';
  required?: boolean;
  labelStyle?: CSSProperties;
  multiline?: boolean;
  errorStyle?: CSSProperties;
  isSecure?: boolean;
  textColor?: string;
  labelTextColor?: string;
  rightAccessory?: () => React.ReactNode;
  typeText?: string;
  insideLeftIcon?: () => React.ReactNode;
  showError?: boolean;
  className?: string;
  inputClassName?: string;
}

const CInput: React.FC<CInputProps> = ({
  value = '',
  label,
  bgColor,
  inputContainerStyle,
  inputBoxStyle,
  onChange,
  placeholder,
  type = 'text',
  onFocus,
  onBlur,
  errorText,
  autoFocus = false,
  maxLength,
  editable = true,
  autoCapitalize,
  required = false,
  labelStyle,
  multiline = false,
  errorStyle,
  isSecure = false,
  textColor,
  labelTextColor,
  rightAccessory,
  typeText,
  insideLeftIcon,
  showError = true,
  className = '',
  inputClassName = '',
  ...props
}) => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [isSecureVisible, setIsSecureVisible] = useState(!isSecure);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const toggleSecureVisibility = () => {
    setIsSecureVisible(!isSecureVisible);
  };

  const InputElement = multiline ? 'textarea' : 'input';

  return (
    <div className={`mb-3 w-full lg:max-w-lg ${className}`}>
      {label && (
        <div
          className="flex justify-between items-center mb-1"
          style={labelStyle}
        >
          <div className="flex flex-row">
            <CText
              style={{ textAlign: 'left', opacity: 0.9 }}
              color={labelTextColor || colors.grayScale400}
              type={typeText || 'r16'}
            >
              {label}
            </CText>
            {required && (
              <CText color={colors.alertColor}> *</CText>
            )}
          </div>
        </div>
      )}

      <div
        className={`
          flex flex-row items-center justify-between w-full rounded-xl border
          ${multiline ? 'min-h-24' : 'h-12 md:h-14'}
        `}
        style={{
          borderColor: errorText ? colors.alertColor : (bgColor || colors.inputBackground),
          backgroundColor: bgColor || colors.inputBackground,
          ...inputContainerStyle,
        }}
      >
        {insideLeftIcon && (
          <div className="pl-3">{insideLeftIcon()}</div>
        )}

        <InputElement
          value={value}
          maxLength={maxLength}
          autoFocus={autoFocus}
          autoCorrect="off"
          autoCapitalize={autoCapitalize}
          placeholder={placeholder}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={!editable}
          type={isSecure && !isSecureVisible ? 'password' : type}
          className={`
            flex-1 p-3 bg-transparent border-none rounded-xl
            text-base font-sans outline-none w-[90%]
            ${multiline ? 'min-h-24 resize-y' : 'h-12 md:h-14 resize-none'}
            ${!editable ? 'cursor-not-allowed' : ''}
            ${inputClassName}
          `}
          style={{
            color: textColor || colors.textColor,
            ...inputBoxStyle,
          }}
          {...(props as any)}
        />

        {rightAccessory && (
          <div className="mr-4">{rightAccessory()}</div>
        )}

        {isSecure && (
          <button
            type="button"
            onClick={toggleSecureVisibility}
            className="bg-transparent border-none cursor-pointer mr-3 flex items-center"
            style={{ color: colors.grayScale500 }}
          >
            {React.createElement(isSecureVisible ? FiEye as any : FiEyeOff as any, { size: 24 })}
          </button>
        )}
      </div>

      {errorText && errorText !== '' && (
        <p
          className="text-xs mt-1 ml-1"
          style={{ color: colors.alertColor, ...errorStyle }}
        >
          {errorText}
        </p>
      )}

      {maxLength && showError && value.length > maxLength && (
        <p
          className="text-xs mt-1 ml-1"
          style={{ color: colors.alertColor, ...errorStyle }}
        >
          It should be maximum {maxLength} characters
        </p>
      )}
    </div>
  );
};

export default CInput;
